/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {stringHelper} from '../helpers/string.helper';
import {urlHelper} from '../helpers/url.helper';

/**
 * A bridge layer service which used to retrieve metadata exposed by SFCC server.
 */
export const dwService = {
  /**
   * @returns {string} Current session currency code
   */
  get currency() {
    return window.__site?.currency;
  },

  /**
   * @returns {string} Current session locale ID
   */
  get locale() {
    return window.__site?.locale;
  },

  /**
   * @returns {boolean}
   */
  get loaded() {
    return Object.hasOwn(window, '__site');
  },

  /**
   * Gets all preferences exposed by server
   *
   * @returns {object} The preferences object
   */
  get preferences() {
    return window.__site?.preferences || {};
  },

  /**
   * Gets all resource bundles exposed by server
   *
   * @returns {object} The resource bundles
   */
  get resources() {
    return window.__site?.resources || {};
  },

  /**
   * Gets all urls exposed by server
   *
   * @returns {object} The urls object
   */
  get urls() {
    return window.__site?.urls || {};
  },

  /**
   * Checks if the given preference name is exposed by server or not.
   *
   * @param {string} name The preference name to check
   *
   * @returns {boolean} `true` if preference is exposed by server
   */
  hasPreference(name) {
    return Object.hasOwn(this.preferences, name);
  },

  /**
   * Returns a custom preference value.
   * If the preference is missing or nullish, returns `defaultValue` when provided,
   * otherwise `null`.
   *
   * @param {string|string[]} name The preference name
   * @param {any} [defaultValue] Fallback when preference is missing or nullish
   *
   * @returns {any} The configured preference value
   */
  preference(name, defaultValue) {
    if (Array.isArray(name)) {
      return name.reduce((preferences, key) => {
        preferences[key] = this.preference(key);

        return preferences;
      }, {});
    }

    const value = this.hasPreference(name) ? this.preferences[name] : null;

    if (value == null && arguments.length > 1) {
      return defaultValue;
    }

    return value;
  },

  /**
   * Checks if the given resource bundle name is exposed by server or not.
   *
   * @example
   *
   * ```ts
   *  hasBundle('account')     // true
   *  hasBundle('forms')       // true
   *  hasBundle('not-exposed') // false
   * ```
   *
   * @param {string} name The resource bundle name to check
   *
   * @returns {boolean} `true` if bundle is exposed by server
   */
  hasBundle(name) {
    return Object.hasOwn(this.resources, name);
  },

  /**
   * Gets resource bundle by the given name.
   *
   * @param {string} name The resource bundle name to retrieve
   *
   * @returns {object|null} Return the resource bundle object or `null` if not found.
   */
  getBundle(name) {
    return this.hasBundle(name) ? this.resources[name] : null;
  },

  /**
   * Checks for a specific resource key from a bundle is exposed by server or not.
   *
   * @example
   *
   * ```ts
   *  hasResource('label.email', 'login') // true
   *  hasResource('missing.key', 'login') // false
   *  hasResource('label.email', 'missing-bundle') // false
   * ```
   *
   * @param {string} key    The resource key
   * @param {string} bundle The resource bundle name
   *
   * @returns {boolean} `true` if resource found in bundle
   */
  hasResource(key, bundle) {
    return this.hasBundle(bundle) && Object.hasOwn(this.resources[bundle], key);
  },

  /**
   * Gets a specific resource key from a bundle if is's exposed by server.
   *
   * @example
   *
   * ```ts
   *  getResource('label.email', 'login') // Email:
   *  getResource('missing-key', 'login') // null
   *  getResource('error.too-short', 'forms') // Please enter at least {0} characters.
   *  getResource('label.email', 'missing-bundle') // null
   * ```
   *
   * @param {string} key    The resource key
   * @param {string} bundle The resource bundle name
   *
   * @returns {string|null} Return the resource message or `null` if not found.
   */
  getResource(key, bundle) {
    return this.hasResource(key, bundle) ? this.resources[bundle][key] : null;
  },

  /**
   * An alias of message method.
   *
   * @param {string}  key    The resource key
   * @param {string}  bundle The resource bundle name
   * @param  {...any} args   List of arguments which are included into the result string
   *
   * @returns {string|null} Return the resource message or `null` if not found.
   */
  msg(key, bundle, ...args) {
    return this.message(key, bundle, ...args);
  },

  /**
   * Gets and format message for specific resource key and bundle.
   *
   * @example
   *
   * ```ts
   *  message('label.email', 'login') // Email:
   *  message('missing-key', 'login') // null
   *  message('error.too-short', 'forms', 16) // Please enter at least 16 characters.
   *  message('label.email', 'missing-bundle') // null
   * ```
   *
   * @param {string}  key    The resource key
   * @param {string}  bundle The resource bundle name
   * @param  {...any} args   List of arguments which are included into the result string
   *
   * @returns {string|null} Return the resource message or `null` if not found.
   */
  message(key, bundle, ...args) {
    const message = this.getResource(key, bundle);

    return message && args.length ? stringHelper.format(message, ...args) : message;
  },

  /**
   * Checks if the given controller action is exposed by server or not.
   *
   * @example
   *
   * ```ts
   *  hasUrl('Home-Show')      // true
   *  hasUrl('Login-Show')     // true
   *  hasUrl('Missing-Action') // false
   * ```
   *
   * @param {string} action The action name to check
   *
   * @returns {boolean} `true` if action is exposed by server
   */
  hasUrl(action) {
    return Object.hasOwn(this.urls, action);
  },

  /**
   * Gets and format url for the given action and params object.
   *
   * @example
   *
   * ```ts
   *  url('Home-Show')
   *  // /on/demandware.store/Sites-sws-au-Site/en_AU/Home-Show
   *
   *  url('Product-Show', { pid: 1234, format: 'ajax' })
   *  // /on/demandware.store/Sites-sws-au-Site/en_AU/Product-Show?pid=1234&format=ajax
   *
   *  url('Missing-Action') // null
   * ```
   *
   * @param {string}  action The action name
   * @param {object}  params The parameters object
   *
   * @returns {string|null} Return the url or `null` if not found.
   */
  url(action, params = {}) {
    return this.hasUrl(action) ? urlHelper.appendParams(this.urls[action], params) : null;
  },
};
